import Link from "next/link";
import type { ComponentPropsWithoutRef } from "react";

/** Dotted-underline inline action — the text-styled control for small
 *  in-context actions ("assign", a row's id, a download) where a pill
 *  would shout. Renders <Link> given `href`, else a plain button. */
/** The inline text action's look alone, for a control that is its own
 *  element — a `WriteSubmit` with no variant wearing it, so a form's small
 *  in-row submit reads as an InlineAction and not a pill. */
export const INLINE_ACTION_CLASS =
  "cursor-pointer font-mono text-xs text-fg underline decoration-dotted decoration-fg-subtle/60 underline-offset-2 transition-colors hover:text-fg-muted disabled:cursor-default disabled:opacity-50";

export function InlineAction({
  href,
  className = "",
  children,
  ...props
}: (ComponentPropsWithoutRef<"button"> & ComponentPropsWithoutRef<"a">) & {
  href?: string;
}) {
  const cls = `${INLINE_ACTION_CLASS} ${className}`.trim();
  if (href !== undefined) {
    // Router Link for in-app routes; plain <a> for external / blob /
    // signed URLs (Link would try to prefetch and route them).
    if (href.startsWith("/")) {
      return (
        <Link
          href={href}
          className={cls}
          {...(props as ComponentPropsWithoutRef<"a">)}
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className={cls}
        {...(props as ComponentPropsWithoutRef<"a">)}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      {...(props as ComponentPropsWithoutRef<"button">)}
    >
      {children}
    </button>
  );
}
